import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, Loader2, Sparkles, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIMEZONES = [
  'Africa/Johannesburg',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Dubai',
  'Australia/Sydney',
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  
  const [tab, setTab] = useState(inviteCode ? 'join' : 'create');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    forum_name: '',
    invite_code: inviteCode || '',
    country: '',
    home_timezone: 'America/New_York',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (tab === 'create' && !formData.forum_name) {
      toast.error('Please enter a forum name');
      return;
    }
    
    if (tab === 'join' && !formData.invite_code) {
      toast.error('Please enter an invite code');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        country: formData.country || null,
        home_timezone: formData.home_timezone,
      };
      
      if (tab === 'create') {
        payload.forum_name = formData.forum_name;
      } else {
        payload.invite_code = formData.invite_code;
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, payload);
      
      // Store token and redirect
      localStorage.setItem('auth_token', response.data.access_token);
      
      toast.success(tab === 'create' 
        ? 'Forum created! Welcome to Forum Planner.' 
        : 'Welcome to the forum!');
      
      // Refresh page to trigger auth context - redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-secondary/30">
      <div className="w-full max-w-lg animate-fade-in">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground">
              <Sparkles className="w-6 h-6" />
            </div>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Forum Planner</h1>
          <p className="text-muted-foreground mt-2">Start your 45-day free trial</p>
        </div>

        <Card className="shadow-xl border-border/50">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Create your account</CardTitle>
            <CardDescription>
              Create a new forum or join an existing one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="create" className="gap-2" data-testid="create-forum-tab">
                  <Plus className="w-4 h-4" />
                  Create Forum
                </TabsTrigger>
                <TabsTrigger value="join" className="gap-2" data-testid="join-forum-tab">
                  <Users className="w-4 h-4" />
                  Join Forum
                </TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Common fields */}
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="register-name-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="register-email-input"
                    required
                  />
                </div>

                <TabsContent value="create" className="mt-0 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forum_name">Forum Name *</Label>
                    <Input
                      id="forum_name"
                      placeholder="e.g., My Forum Group"
                      value={formData.forum_name}
                      onChange={(e) => setFormData({ ...formData, forum_name: e.target.value })}
                      data-testid="forum-name-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      You'll be the forum chair and can invite members later.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="join" className="mt-0 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite_code">Invite Code *</Label>
                    <Input
                      id="invite_code"
                      placeholder="Enter invite code"
                      value={formData.invite_code}
                      onChange={(e) => setFormData({ ...formData, invite_code: e.target.value })}
                      data-testid="invite-code-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Ask your forum chair for the invite code.
                    </p>
                  </div>
                </TabsContent>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    placeholder="e.g., United States"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    data-testid="country-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Home Timezone</Label>
                  <Select
                    value={formData.home_timezone}
                    onValueChange={(value) => setFormData({ ...formData, home_timezone: value })}
                  >
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full gap-2" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {tab === 'create' ? 'Create Forum & Start Trial' : 'Join Forum'}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </Tabs>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By registering, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
